import { SerializedComponent } from "../../components/SerializedApp";
import { ArrayScreenFormat, TableGlideType } from "../description";
import {
    FallbackInitialsSummaryArrayContentDescription,
    FallbackInitialsSummaryArrayScreenDescription,
    FallbackInitialsSummaryArrayScreenHandler,
    isolateFallbackInitialsSummaryArrayContentDescription,
} from "./summary-array-screen";
import { ListItemFlags } from "./list-item-flags";
import { ComponentBuilder, PropertyTableGetter, PropertyDescriptor } from "../components";
import { handleTruncationAndGrouping } from "./truncation-lib";
import { makeEmptyMessage, InlineListBuild, FixedArrayContent } from "./array-content";
import { handleActionIcon } from "../builder-utils";
import { SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { makeEnumProperty } from "../components/property-utils";
import { ListItemSize } from "../components/list-types";
import { ListArrayContentDescription } from "./list-array-screen";

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

type SmallListArrayContentDescription = FallbackInitialsSummaryArrayContentDescription;
type SmallListArrayScreenDescription = FallbackInitialsSummaryArrayScreenDescription;

export class SmallListArrayScreenHandler extends FallbackInitialsSummaryArrayScreenHandler<
    SmallListArrayContentDescription,
    SmallListArrayScreenDescription
> {
    protected readonly supportsNonURLImages = false;
    protected readonly supportsEmojiImages = true;
    protected readonly supportsTruncateList = true;

    constructor() {
        super(ArrayScreenFormat.SmallList, "Compact", "compactList", false);
    }

    public get isLegacy(): boolean {
        return true;
    }

    protected get supportsGrouping(): boolean {
        return true;
    }

    protected contentWithFallbackInitials(
        builder: ComponentBuilder,
        desc: SmallListArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        initials: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        // eslint-disable-next-line prefer-const
        let { icon, flags } = this.makeIconAndFlagsForDefaultAction(
            b,
            desc,
            ListItemFlags.SmallImage | ListItemFlags.DrawSeparator | ListItemFlags.HorizontalLayout
        );

        flags |= ListItemFlags.DisableLongPress;

        const allowWrapping = allowWrappingPropertyHandler.getSwitch(desc);

        if (allowWrapping) {
            flags |= ListItemFlags.WrapText;
        }

        return handleTruncationAndGrouping(
            builder,
            desc,
            table,
            arrayData,
            inlineListBuild,
            caption,
            1,
            true,
            (listData, isGrouped, a) =>
                b.ui("SmallList", {
                    list: listData,
                    caption,
                    emptyMessage: makeEmptyMessage(b, searchActiveFlag, inlineListBuild),
                    isGrouped,
                    allowWrapping: b.constant(allowWrapping),
                    item: handleActionIcon(b, onTap, icon, true, (ot, ic) =>
                        b.adaptiveListItem(
                            title,
                            subtitle,
                            undefined,
                            image,
                            ic,
                            initials,
                            ot,
                            onDropImage,
                            undefined,
                            b.constant(flags),
                            undefined,
                            undefined,
                            itemID
                        )
                    ),
                    ...a,
                })
        );
    }

    public getAdditionalPropertyDescriptors<T extends SmallListArrayContentDescription>(
        _getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        _desc: T
    ): ReadonlyArray<PropertyDescriptor> {
        const descrs: PropertyDescriptor[] = [];
        descrs.push(allowWrappingPropertyHandler);

        return descrs;
    }

    public fixContentDescription(gridContent: SmallListArrayContentDescription): FixedArrayContent | undefined {
        return {
            properties: {
                ...isolateFallbackInitialsSummaryArrayContentDescription(gridContent),
                captionProperty: undefined,
                itemSize: makeEnumProperty(ListItemSize.Compact),
            } as ListArrayContentDescription,
            format: ArrayScreenFormat.List,
        };
    }
}
